import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Printer, Signature, UploadSimple, CheckCircle, X } from "@phosphor-icons/react";
import { SCAN_ACCEPT, buildScanForm } from "./SignedScanUploadButton";
import { MobileScanQR } from "./MobileScanQR";
import { MailboxScanPicker } from "./MailboxScanPicker";
import { Input } from "@/components/ui/input";

function SignersEditor({ signers, setSigners, suggestions }) {
  const unused = (suggestions || []).filter((s) => !signers.some((x) => x.name === s.name));
  const update = (i, patch) => setSigners(signers.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  return (
    <div className="space-y-1.5" data-testid="wet-signers">
      <div className="text-[11px] font-semibold text-slate-700">Unterzeichner (max. 3)</div>
      {signers.map((s, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input value={s.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Name"
                 className="h-8 text-xs" data-testid={`wet-signer-name-${i}`} />
          <Input value={s.role} onChange={(e) => update(i, { role: e.target.value })} placeholder="Rolle"
                 className="h-8 text-xs w-36" data-testid={`wet-signer-role-${i}`} />
          <button type="button" disabled={signers.length <= 1} onClick={() => setSigners(signers.filter((_, j) => j !== i))}
                  className="text-slate-400 hover:text-rose-600 disabled:opacity-30" data-testid={`wet-signer-remove-${i}`}>
            <X size={14} />
          </button>
        </div>
      ))}
      {signers.length < 3 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {unused.map((s) => (
            <button key={s.name} type="button" onClick={() => setSigners([...signers, { name: s.name, role: s.role }])}
                    className="text-[11px] px-2 py-0.5 rounded-full border border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-50"
                    data-testid={`wet-signer-suggest-${s.kind}`}>
              + {s.name}{s.role ? ` (${s.role})` : ""}
            </button>
          ))}
          <button type="button" onClick={() => setSigners([...signers, { name: "", role: "" }])}
                  className="text-[11px] text-teal-700 hover:underline" data-testid="wet-signer-add">
            + Unterzeichner
          </button>
        </div>
      )}
    </div>
  );
}

// Original unterschreiben: drucken → unterschreiben → Scan hochladen (oder bei Post nachreichen)
export default function WetSignaturePanel({ wet, channels, disabled }) {
  const { enabled, setEnabled, scan, setScan, pending, setPending, onPrint,
          signers, setSigners, suggestions } = wet;
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const postOnly = channels.post && !channels.email;

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const { data } = await api.post("/messages/signed-scans", buildScanForm(files), {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScan({ ...data, names: Array.from(files).map((f) => f.name).join(", ") });
      setPending(false);
      toast.success("Scan hochgeladen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan-Upload fehlgeschlagen");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const print = async () => {
    setPrinting(true);
    try { await onPrint(); } finally { setPrinting(false); }
  };

  if (disabled) return null;
  return (
    <div className="border border-indigo-200 rounded-md p-3 bg-indigo-50/40" data-testid="wet-sign-panel">
      <label className="flex items-start gap-2 cursor-pointer">
        <Checkbox checked={enabled} onCheckedChange={(v) => setEnabled(!!v)} data-testid="wet-sign-toggle" />
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm font-semibold"><Signature size={14} /> Original unterschreiben</div>
          <div className="text-[11px] text-slate-500">
            Dokument ausdrucken, handschriftlich unterschreiben und den Scan einfügen. Gespeichert
            {channels.email ? " und per E-Mail versendet" : ""} wird das unterschriebene Exemplar.
          </div>
        </div>
      </label>
      {enabled && (
        <div className="mt-3 space-y-2 pl-6">
          {setSigners && <SignersEditor signers={signers} setSigners={setSigners} suggestions={suggestions} />}
          <Button size="sm" variant="outline" onClick={print} disabled={printing} className="gap-1.5" data-testid="wet-sign-print">
            <Printer size={14} /> {printing ? "Wird erzeugt…" : "1. Zum Unterschreiben drucken"}
          </Button>
          <div className="flex items-center gap-2 flex-wrap">
            <input ref={inputRef} type="file" multiple accept={SCAN_ACCEPT} className="hidden"
                   onChange={(e) => upload(e.target.files)} data-testid="wet-sign-file" />
            <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}
                    className="gap-1.5" data-testid="wet-sign-upload">
              <UploadSimple size={14} /> {uploading ? "Lädt hoch…" : scan ? "Scan ersetzen" : "2. Scan einfügen (PDF oder Fotos)"}
            </Button>
            <MobileScanQR label={wet.label} onReceived={(d) => { setScan(d); setPending(false); }} testId="wet-sign-mobile" />
            <MailboxScanPicker endpoint={(mid) => `/messages/signed-scans/from-mailbox/${mid}`}
                               onPicked={(d) => { setScan(d); setPending(false); }} testId="wet-sign-mailbox" />
            {scan && (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-800" data-testid="wet-sign-scan-ok">
                <CheckCircle size={14} weight="fill" /> {scan.pages} Seite{scan.pages === 1 ? "" : "n"} · {scan.names}
                <button type="button" onClick={() => setScan(null)} className="text-slate-400 hover:text-rose-600" title="Scan entfernen" data-testid="wet-sign-scan-remove">
                  <X size={12} />
                </button>
              </span>
            )}
          </div>
          {postOnly && !scan && (
            <label className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
              <Checkbox checked={pending} onCheckedChange={(v) => setPending(!!v)} data-testid="wet-sign-pending" />
              Scan wird nachgereicht (Aufgabe wird für mich angelegt)
            </label>
          )}
          {channels.email && !scan && (
            <div className="text-[11px] text-amber-800" data-testid="wet-sign-email-hint">
              Für den E-Mail-Versand muss der Scan vorliegen — er ersetzt die Brief-Anlage.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function wetSignReady(wet, channels) {
  if (!wet?.enabled) return true;
  if (wet.scan) return true;
  return !channels.email && channels.post && wet.pending;
}
